import axios from 'axios';
import { BACKEND_URL, PYTHON_BACKEND_URL } from '../../src/constants'

// Verbose VTN call logging. Default OFF in production builds so transaction
// references and payloads never leak into a shared/observed browser console.
// Opt in for local debugging by setting REACT_APP_DEBUG_VTN=true.
const DEBUG_VTN =
  (typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production') &&
  (process.env?.REACT_APP_DEBUG_VTN === 'true');

// URL prefixes that belong to the member-verification flow. The
// memberVerificationToken in localStorage is ONLY attached to requests under
// these prefixes. Previously the token was sent on every request and took
// precedence over `accessToken`, so a lingering member token would silently
// override an authenticated admin/company session. See the route guards in
// frontend/src/guards/* — those identify the flows that need each token.
//
// IMPORTANT: do not add a bare `verification/` here. The `/verification/*`
// namespace is shared between member-flow endpoints (member token) and
// admin/company-management endpoints like `verification/companies-with-members`
// which need the standard accessToken. List the member-flow paths explicitly
// so admin endpoints fall through to the accessToken branch.
const MEMBER_VERIFICATION_PATH_PREFIXES = [
  'public/',
  'verification/end-session',
  'verification/start-session',
  'verification/page-close',
  'verification/request-otp-fallback',
  'verification/verify-otp-fallback',
  'verification/resend-otp-fallback',
  'verification/check-otp-eligibility/',
  'verification/compare-faces',
  'internal/verification/verify-bvn',
  'api/verify/',     // Python backend, member-verification flow
  'api/quality/',
  'api/liveliness/',
];

function _isMemberVerificationRoute(url) {
  const u = String(url || '').replace(/^\/+/, '');
  return MEMBER_VERIFICATION_PATH_PREFIXES.some((p) => u.startsWith(p));
}

function debugLog(method, url, body, result) {
  if (!DEBUG_VTN) return;
  if (!url.includes('vtn/')) return;
  const isError = !result.status;
  const logFn = isError ? console.error : console.log;
  const tag = isError ? '❌' : '✅';
  logFn(
    `%c[VTN DEBUG] ${tag} ${method} /${url}`,
    `color: ${isError ? '#ff6b6b' : '#10b981'}; font-weight: bold;`
  );
  if (body && Object.keys(body).length > 0) console.log('  → Request body:', body);
  console.log('  ← Status:', result.res?.status ?? '(no status)');
  console.log('  ← Data:', result.res?.data ?? result.msg ?? '(empty)');
  if (isError) console.log('  ← Full error response:', result.res);
}

// Surface backend billing-error codes globally so individual screens don't
// each have to remember to translate the 402 payload. Listeners (App-level
// SnackbarProvider hookup, banner components, etc.) subscribe to the
// 'billing-error' CustomEvent and decide how to render.
function dispatchBillingError(method, url, result) {
  if (typeof window === 'undefined') return;
  const data = result?.res?.data;
  const code = data?.error_code;
  if (!code) return;
  // Only billing/auth-related codes — anything else is the screen's own concern.
  const billingCodes = new Set([
    'VTN_DEBIT_LIMIT_EXCEEDED',
    'INSUFFICIENT_BALANCE',
    'BILLING_NOT_AUTHORIZED',
    'BILLING_NOT_CONFIGURED',
    'NO_PRICING_TIER',
  ]);
  if (!billingCodes.has(code)) return;
  try {
    window.dispatchEvent(new CustomEvent('billing-error', {
      detail: { code, payload: data, method, url }
    }));
  } catch (_) { /* CustomEvent unsupported — best-effort */ }
}

export function CallAPI(method, url, body = '', headercustom=null, endpoint) {
  let apiUrl = ''
  if (url.startsWith('api/')) { // Check if the URL is for the Python backend
    // Remove 'api/' prefix since PYTHON_BACKEND_URL already includes it
    apiUrl = PYTHON_BACKEND_URL + url.substring(4);
  }
  else
  {
    // Ensure proper concatenation - remove leading slash if present since BACKEND_URL has trailing slash
    apiUrl = BACKEND_URL + (url.startsWith('/') ? url.substring(1) : url);
  }
  
  // Configure axios to include credentials (cookies) in requests
  const axiosConfig = {
    withCredentials: true, // This ensures cookies are sent with requests
    headers: {}
  };
  
  // Auth token selection. The member-verification token is ONLY used on
  // member-flow URLs; everything else uses the authenticated user's
  // accessToken. Previously the member token took precedence on every
  // request, so a lingering member token from a prior verification session
  // silently overrode the admin/company login on the same browser.
  if (headercustom) {
    // Priority 1: explicit override (admin impersonation, server-to-server).
    axiosConfig.headers.Authorization = `Bearer ${headercustom}`;
  } else if (_isMemberVerificationRoute(url)) {
    // Priority 2: member-verification flow.
    const memberToken = localStorage.getItem('memberVerificationToken');
    if (memberToken) {
      axiosConfig.headers.Authorization = `Bearer ${memberToken}`;
    }
  } else {
    // Priority 3: authenticated user (admin / company).
    const token = localStorage.getItem('accessToken');
    if (token) {
      axiosConfig.headers.Authorization = `Bearer ${token}`;
    }
  }
  
  return new Promise(async (resolve, reject) => {
    try {
        if (method === "POST") {
          axios
            .post(apiUrl, body, axiosConfig)
            .then((response) => {
              const result = (response.status === 200 || response.status === 201)
                ? { status: true, res: response, msg: response?.data?.message }
                : { status: false, res: response, msg: response?.data?.message };
              debugLog(method, url, body, result);
              resolve(result);
            })
            .catch((err) => {
              const res = err?.response;
              const msg = res?.status === 429
                ? (res?.data?.message || 'Too many requests. Please slow down and try again.')
                : res?.data?.message;
              const result = { status: false, res, msg };
              debugLog(method, url, body, result);
              dispatchBillingError(method, url, result);
              resolve(result);
            });
        }
        if (method === "PUT") {
          axios
            .put(apiUrl, body, axiosConfig)
            .then((response) => {
              const result = (response.status === 200 || response.status === 201)
                ? { status: true, res: response, msg: response?.data?.message }
                : { status: false, res: response, msg: response?.data?.message };
              debugLog(method, url, body, result);
              resolve(result);
            })
            .catch((err) => {
              const res = err?.response;
              const msg = res?.status === 429
                ? (res?.data?.message || 'Too many requests. Please slow down and try again.')
                : res?.data?.message;
              const result = { status: false, res, msg };
              debugLog(method, url, body, result);
              dispatchBillingError(method, url, result);
              resolve(result);
            });
        }
        if (method === "PATCH") {
          axios
            .patch(apiUrl, body, axiosConfig)
            .then((response) => {
              const result = (response.status === 200 || response.status === 201)
                ? { status: true, res: response, msg: response?.data?.message }
                : { status: false, res: response, msg: response?.data?.message };
              debugLog(method, url, body, result);
              resolve(result);
            })
            .catch((err) => {
              const res = err?.response;
              const msg = res?.status === 429
                ? (res?.data?.message || 'Too many requests. Please slow down and try again.')
                : res?.data?.message;
              const result = { status: false, res, msg };
              debugLog(method, url, body, result);
              dispatchBillingError(method, url, result);
              resolve(result);
            });
        }
        if (method === "GET") {
          axios
            .get(apiUrl, axiosConfig)
            .then((response) => {
              const result = (response.status === 200 || response.status === 201)
                ? { status: true, res: response, msg: response?.data?.message }
                : { status: false, res: response, msg: response?.data?.message };
              debugLog(method, url, null, result);
              resolve(result);
            })
            .catch((err) => {
              const res = err?.response;
              const msg = res?.status === 429
                ? (res?.data?.message || 'Too many requests. Please slow down and try again.')
                : res?.data?.message;
              const result = { status: false, res, msg };
              debugLog(method, url, null, result);
              dispatchBillingError(method, url, result);
              resolve(result);
            });
        }
        if (method === "DELETE") {
          let config = axiosConfig
          if (body) {
            config = { ...axiosConfig, data: body }
          }
          axios
            .delete(apiUrl, config)
            .then((response) => {
              const result = (response.status === 200 || response.status === 201)
                ? { status: true, res: response, msg: response?.data?.message }
                : { status: false, res: response, msg: response?.data?.message };
              debugLog(method, url, body, result);
              resolve(result);
            })
            .catch((err) => {
              const res = err?.response;
              const msg = res?.status === 429
                ? (res?.data?.message || 'Too many requests. Please slow down and try again.')
                : res?.data?.message;
              const result = { status: false, res, msg };
              debugLog(method, url, body, result);
              dispatchBillingError(method, url, result);
              resolve(result);
            });
        }
    } catch (err) {
      reject(err.response);
    }
  });
}

export default CallAPI;
